import { useQuery } from "@tanstack/react-query";

import { AZURE_MJ } from "@/lib/axios";

export const COST_QUERY_KEYS = {
  getCostSummary: "COST_GET_COST_SUMMARY"
};

/** The four cost categories the BFF can return in `costs[].type`. */
export type CostType = "Labour" | "Plant" | "Material" | "Subcontractor";

export interface ICostSummaryItem {
  /** One of `CostType`, but typed loosely so an unknown type never breaks parsing. */
  type: CostType | string;
  amount: number;
}

/** Raw shape returned by `GET /cost/summary`. `costs` may be empty. */
export interface ICostSummaryRes {
  jobNo: string;
  jobName: string;
  month: string;
  costs: ICostSummaryItem[];
}

/** Flattened per-category totals consumed by the UI. */
export interface ICostByType {
  labour: number;
  plant: number;
  material: number;
  subcontractor: number;
}

/**
 * Collapse the `costs` array into per-category totals.
 *
 * - Any of the four categories may be missing → defaults to 0.
 * - The array may be empty → all zeros.
 * - Duplicate types are summed; unknown types are ignored.
 */
export const toCostByType = (costs?: ICostSummaryItem[]): ICostByType => {
  const totals: ICostByType = {
    labour: 0,
    plant: 0,
    material: 0,
    subcontractor: 0
  };

  costs?.forEach(({ type, amount }) => {
    switch (type) {
      case "Labour":
        totals.labour += amount;
        break;
      case "Plant":
        totals.plant += amount;
        break;
      case "Material":
        totals.material += amount;
        break;
      case "Subcontractor":
        totals.subcontractor += amount;
        break;
      default:
        break;
    }
  });

  return totals;
};

/**
 * Fetches the actual incurred cost summary for a job/month from the BFF and
 * returns it as per-category totals (`ICostByType`).
 *
 * @param jobNo project number, e.g. `"0247"` (from `selectedProject.value`)
 * @param month `"YYYY-MM"`
 */
export const useGetCostSummary = (jobNo?: string, month?: string) => {
  return useQuery({
    queryKey: [COST_QUERY_KEYS.getCostSummary, jobNo, month],
    queryFn: async (): Promise<ICostSummaryRes> => {
      const { data } = await AZURE_MJ.get("/cost/summary", {
        params: { jobNo, month }
      });
      return data;
    },
    select: (data) => toCostByType(data?.costs),
    enabled: !!jobNo && !!month
  });
};
